import type {
	AdaptiveCardElementJson,
	AdaptiveCardJson,
	DibotCustomEvent,
	DibotProduct,
} from '@/components/content/DibotChat/types';
import { normalize } from 'presentation/events/utils/stringUtils';
import {
	BOT_ESCALATED_SESSION_EVENT,
	BOT_SESSION_TOPIC_EVENT,
	GA4_BOT_CHANNEL_WEB,
	NOT_AVAILABLE,
	PRODUCTOS_DATA_ID,
	RECOMMENDATION_HEADER_REGEX,
	SKU_HEADER_REGEX,
	SKU_INPUT_REGEX,
} from './constants';
import {
	GA4_BOT_TOPIC_CATALOG,
	type DibotProductCardKind,
	type GA4BotEscalatedSessionEvent,
	type GA4BotProductSearchEventPayload,
	type GA4BotSearchResultsEventPayload,
	type GA4BotSearchTopic,
	type GA4BotSessionTopicEvent,
	type GA4BotSuccessfulTransfer,
	type GA4BotTopicPayload,
} from './types';

export const getProductosDataBlock = (
	card?: AdaptiveCardJson
): AdaptiveCardElementJson | undefined =>
	card?.body?.find((item) => item.id === PRODUCTOS_DATA_ID && item.isVisible === false);

export const isProductResultsCard = (card?: AdaptiveCardJson): boolean =>
	!!getProductosDataBlock(card);

export const getCardHeaderText = (card?: AdaptiveCardJson): string =>
	card?.body?.find((item) => item.type === 'TextBlock' && item.isVisible !== false)?.text?.trim() ??
	'';

export const parseProductsFromCard = (card?: AdaptiveCardJson): DibotProduct[] => {
	try {
		const parsed = JSON.parse(getProductosDataBlock(card)?.text ?? '[]');
		return Array.isArray(parsed) ? (parsed as DibotProduct[]) : [];
	} catch {
		return [];
	}
};

export const extractSkusFromCard = (card?: AdaptiveCardJson): string[] => {
	const skus = parseProductsFromCard(card)
		.map(({ sku }) => {
			if (sku === null || sku === undefined) return '';
			return typeof sku === 'number' ? String(Math.trunc(sku)) : String(sku).trim();
		})
		.filter((sku) => sku !== '');
	return Array.from(new Set(skus));
};

export const classifyProductCard = (card?: AdaptiveCardJson): DibotProductCardKind =>
	RECOMMENDATION_HEADER_REGEX.test(getCardHeaderText(card)) ? 'secondary' : 'primary';

export const resolveSearchTopic = (headerText: string, searchText: string): GA4BotSearchTopic =>
	SKU_HEADER_REGEX.test(headerText) || SKU_INPUT_REGEX.test(searchText.trim())
		? 'THD_SKU'
		: 'THD_Curador';

export const buildBotProductSearchPayload = ({
	sessionId,
	storeId,
	topic,
	product,
}: {
	sessionId: string;
	storeId: string;
	topic: GA4BotSearchTopic;
	product: string;
}): GA4BotProductSearchEventPayload => ({
	event: 'bot_product_search',
	session_id: sessionId,
	store_id: storeId,
	channel: GA4_BOT_CHANNEL_WEB,
	topic,
	product,
});

export const buildBotSearchResultPayload = ({
	sessionId,
	storeId,
	products,
}: {
	sessionId: string;
	storeId: string;
	products: string[];
}): GA4BotSearchResultsEventPayload => ({
	event: 'bot_search_result',
	session_id: sessionId,
	store_id: storeId,
	channel: GA4_BOT_CHANNEL_WEB,
	products,
});

const NA_TOPIC_PAYLOAD: GA4BotTopicPayload = {
	topic: NOT_AVAILABLE,
	topic_type: NOT_AVAILABLE,
	is_main_topic: NOT_AVAILABLE,
};

export const resolveTopicPayload = (rawTopic: string): GA4BotTopicPayload => {
	const catalogKeys = Object.keys(GA4_BOT_TOPIC_CATALOG) as Array<
		keyof typeof GA4_BOT_TOPIC_CATALOG
	>;

	const matchedKey =
		catalogKeys.find((key) => key === rawTopic) ??
		catalogKeys.find((key) => normalize(key) === normalize(rawTopic));

	if (!matchedKey) return NA_TOPIC_PAYLOAD;

	const { topicType, isMainTopic } = GA4_BOT_TOPIC_CATALOG[matchedKey];
	return {
		topic: matchedKey,
		topic_type: topicType,
		is_main_topic: isMainTopic,
	} as GA4BotTopicPayload;
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

export const formatInteractionDate = (ts?: string | Date | null): string => {
	if (ts === null || ts === undefined) return NOT_AVAILABLE;
	const date = ts instanceof Date ? ts : new Date(ts);
	if (Number.isNaN(date.getTime())) return NOT_AVAILABLE;

	return (
		`${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ` +
		`${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`
	);
};

export const extractSessionTopic = (customEvent?: DibotCustomEvent): string | null => {
	if (customEvent?.customEventName !== BOT_SESSION_TOPIC_EVENT) return null;
	const topic = customEvent.customEventValue?.trim();
	return topic ? topic : null;
};

export const buildBotSessionTopicPayload = ({
	sessionId,
	storeId,
	topic,
	interactionWith,
	interactionDate,
}: {
	sessionId: string;
	storeId: string;
	topic: string;
	interactionWith: string;
	interactionDate: string;
}): GA4BotSessionTopicEvent => ({
	event: 'bot_session_topic',
	session_id: sessionId,
	store_id: storeId,
	channel: GA4_BOT_CHANNEL_WEB,
	...resolveTopicPayload(topic),
	interaction_with: interactionWith,
	interaction_date: interactionDate,
});

export const extractEscalatedTopic = (customEvent?: DibotCustomEvent): string | null => {
	if (customEvent?.customEventName !== BOT_ESCALATED_SESSION_EVENT) return null;
	const topic = customEvent.customEventValue?.trim();
	return topic ? topic : null;
};

export const extractMessageText = (content?: string | { message?: string }): string => {
	const raw = typeof content === 'object' ? content?.message : content;
	if (raw === null || raw === undefined) return '';

	try {
		const parsed = JSON.parse(raw);
		if (parsed !== null && typeof parsed === 'object' && typeof parsed.text === 'string') {
			return parsed.text.trim();
		}
	} catch {
		// Not JSON — fall through to the raw string.
	}

	return raw.trim();
};

export const buildBotEscalatedSessionPayload = ({
	sessionId,
	storeId,
	topic,
	userInput,
	botMessage,
	successfulTransfer,
}: {
	sessionId: string;
	storeId: string;
	topic: string;
	userInput: string;
	botMessage: string;
	successfulTransfer: GA4BotSuccessfulTransfer;
}): GA4BotEscalatedSessionEvent => ({
	event: 'bot_escalated_session',
	session_id: sessionId,
	store_id: storeId,
	channel: GA4_BOT_CHANNEL_WEB,
	topic: resolveTopicPayload(topic).topic,
	user_input: userInput || NOT_AVAILABLE,
	bot_message: botMessage || NOT_AVAILABLE,
	successful_transfer: successfulTransfer,
});
